import ufeApi from 'services/api/ufeApi';
import selfReturnUtils from 'utils/SelfReturn';
import { gqlClient } from 'services/api/gql/gqlClient';
import { UpdateShippingAddress } from 'constants/gql/mutations/checkout/updateShippingAddress.gql';

// https://confluence.sephora.com/wiki/pages/viewpage.action?pageId=275023233

const OXS_ADD_URL = '/api/selfReturn/replacementOrders/shippingGroups/shippingAddress';
const OXS_UPDATE_URL = '/api/selfReturn/updateNCR/shippingAddress/updateAddress';

/** @returns {boolean} Whether OIS returns migration is enabled */
const isOisEnabled = () =>
    !!(Sephora?.configurationSettings?.oisMigration?.global?.isEnabled && Sephora?.configurationSettings?.oisMigration?.returns?.isEnabled);

/**
 * Builds the OIS GraphQL input from the OXS payload format.
 *
 * @param {Object} payload - The shipping address payload from callers
 * @param {string} originalOrderId - The originating order ID
 * @returns {Object} The OIS GraphQL input object
 */
const buildInput = (payload, originalOrderId) => {
    const {
        address = {},
        addressId,
        shippingGroupId,
        addressType,
        isDefaultAddress,
        addressValidated,
        replacementOrderId,
        isPOBoxAddress
    } = payload;

    // Callers may pass addressId at the top level (payload.addressId) or nested inside
    // payload.address.addressId (e.g. ShipAddressSection builds address: newAddressData).
    const resolvedAddressId = addressId || address.addressId;

    const input = {
        originalOrderId,
        replacementOrderId,
        shippingGroupId: shippingGroupId || '0',
        addressType: addressType || 'SHIPPING',
        firstName: address.firstName,
        lastName: address.lastName,
        addressLine1: address.address1,
        addressLine2: address.address2,
        city: address.city,
        state: address.state,
        postalCode: address.postalCode,
        country: address.country,
        phone: address.phone,
        isPOBoxAddress: isPOBoxAddress || false,
        isDefaultAddress: isDefaultAddress || false,
        addressValidated: addressValidated || false
    };

    if (resolvedAddressId) {
        input.addressId = resolvedAddressId;
    }

    return input;
};

/**
 * Handles shipping address add/update via OIS GraphQL (UpdateShippingAddress).
 *
 * @param {Object} payload - The shipping address payload
 * @param {string} originalOrderId - The originating order ID
 * @returns {Promise<Object>} The mutation result
 * @throws {Error} Throws with errorCode=-1 and errorMessages for non-SUCCESS responses
 */
const handleOisRequest = async (payload, originalOrderId) => {
    const data = await gqlClient.query({
        operationName: 'UpdateShippingAddress',
        query: UpdateShippingAddress,
        variables: {
            input: buildInput(payload, originalOrderId)
        }
    });

    const result = data?.updateShippingAddress;

    if (!result) {
        throw new Error('Update shipping address: unexpected response shape');
    }

    if (result.responseCode && result.responseCode !== 'SUCCESS') {
        const error = new Error(result.message || 'Update shipping address failed');
        error.errorCode = -1;
        error.responseCode = result.responseCode;
        error.errorMessages = result.message ? [result.message] : [];
        throw error;
    }

    return {
        ...result,
        addressId: result.address?.addressId
    };
};

/**
 * Handles shipping address add/update via legacy OXS REST API.
 *
 * @param {Object} payload - The shipping address payload
 * @param {string} originalOrderId - The originating order ID
 * @param {string} url - The OXS endpoint URL
 * @param {string} method - HTTP method ('POST' for add, 'PUT' for update)
 * @returns {Promise<Object>} The OXS response data
 */
const handleOxsRequest = (payload, originalOrderId, url, method) => {
    const originalOrderIdParam = originalOrderId ? `?originalOrderId=${originalOrderId}` : '';

    return ufeApi.makeRequest(url + originalOrderIdParam, { method, body: JSON.stringify(payload) }, { skipLoginPrompt: true });
};

/**
 * Adds a shipping address to a replacement (NCR) order.
 * Routes to OIS GraphQL when flags are enabled; otherwise falls back to legacy OXS REST.
 *
 * @param {Object} payload - The shipping address payload
 * @returns {Promise<Object>} The shipping address response
 */
export const shippingAddress = payload => {
    const originalOrderId = selfReturnUtils.getOriginalOrderId();

    return isOisEnabled() ? handleOisRequest(payload, originalOrderId) : handleOxsRequest(payload, originalOrderId, OXS_ADD_URL, 'POST');
};

/**
 * Updates a shipping address on a replacement (NCR) order.
 * Routes to OIS GraphQL when flags are enabled; otherwise falls back to legacy OXS REST.
 *
 * @param {Object} payload - The address update payload
 * @returns {Promise<Object>} The update address response
 */
export const updateAddress = payload => {
    const originalOrderId = selfReturnUtils.getOriginalOrderId();

    return isOisEnabled() ? handleOisRequest(payload, originalOrderId) : handleOxsRequest(payload, originalOrderId, OXS_UPDATE_URL, 'PUT');
};
